import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Stack,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Tooltip,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import {
  fetchWholesaleTiers,
  createWholesaleTier,
  updateWholesaleTier,
  deleteWholesaleTier,
  WholesaleTier,
} from "../api/wholesaleApi";

const pct = (v: number | string | undefined) => Number(v ?? 0);

type EditState = {
  open: boolean;
  tier: WholesaleTier | null; // null = creating
  name: string;
  description: string;
  discount: string;
};

const emptyEdit: EditState = {
  open: false,
  tier: null,
  name: "",
  description: "",
  discount: "",
};

const WholesaleTiersPage: React.FC = () => {
  const [tiers, setTiers] = useState<WholesaleTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<EditState>(emptyEdit);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WholesaleTier | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTiers(await fetchWholesaleTiers());
    } catch (err) {
      console.error(err);
      toast.error("Failed to load tiers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () =>
    setEdit({ open: true, tier: null, name: "", description: "", discount: "" });

  const openEdit = (t: WholesaleTier) =>
    setEdit({
      open: true,
      tier: t,
      name: t.name,
      description: t.description ?? "",
      discount: String(pct(t.discount_pct)),
    });

  const closeEdit = () => setEdit(emptyEdit);

  const handleSave = async () => {
    const name = edit.name.trim();
    if (!name) {
      toast.error("Tier name is required");
      return;
    }
    const isDefault = edit.tier?.is_default;
    const discountNum = isDefault ? 0 : Math.min(100, Math.max(0, Number(edit.discount) || 0));

    setSaving(true);
    try {
      if (edit.tier) {
        await updateWholesaleTier(edit.tier.id, {
          name,
          description: edit.description.trim() || null,
          discount_pct: discountNum,
        });
        toast.success("Tier updated");
      } else {
        await createWholesaleTier({
          name,
          description: edit.description.trim() || null,
          discount_pct: discountNum,
        });
        toast.success("Tier created");
      }
      closeEdit();
      await load();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.error || "Failed to save tier");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteWholesaleTier(deleteTarget.id);
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
      await load();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.error || "Failed to delete tier");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Wholesale Tiers
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New tier
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        The <strong>default</strong> tier holds the base wholesale price you set on each
        product. Other tiers take a discount off that base automatically — set each tier's
        discount below. You can still override an exact price for a specific product on the
        product page.
      </Alert>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {tiers.map((t) => (
            <Card key={t.id} variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                      <Typography variant="h6">{t.name}</Typography>
                      {t.is_default && <Chip size="small" label="Default" color="primary" />}
                      {!t.is_default && (
                        <Chip
                          size="small"
                          label={`${pct(t.discount_pct)}% off base`}
                          color={pct(t.discount_pct) > 0 ? "success" : "default"}
                          variant="outlined"
                        />
                      )}
                      <Chip
                        size="small"
                        label={`${t.customer_count ?? 0} customer${(t.customer_count ?? 0) === 1 ? "" : "s"}`}
                        variant="outlined"
                      />
                    </Stack>
                    {t.description && (
                      <Typography variant="body2" color="text.secondary">
                        {t.description}
                      </Typography>
                    )}
                    {t.is_default && (
                      <Typography variant="caption" color="text.secondary">
                        Base pricing — no discount applied.
                      </Typography>
                    )}
                  </Box>

                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => openEdit(t)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t.is_default ? "The default tier can't be deleted" : "Delete"}>
                      <span>
                        <IconButton
                          onClick={() => setDeleteTarget(t)}
                          size="small"
                          color="error"
                          disabled={t.is_default}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Create / edit dialog */}
      <Dialog open={edit.open} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>{edit.tier ? "Edit tier" : "New tier"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Tier name"
              value={edit.name}
              onChange={(e) => setEdit((s) => ({ ...s, name: e.target.value }))}
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Description"
              value={edit.description}
              onChange={(e) => setEdit((s) => ({ ...s, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Discount off base price"
              value={edit.tier?.is_default ? "0" : edit.discount}
              onChange={(e) => setEdit((s) => ({ ...s, discount: e.target.value }))}
              type="number"
              fullWidth
              disabled={edit.tier?.is_default}
              helperText={
                edit.tier?.is_default
                  ? "The default tier is the base price — no discount."
                  : "Applied to every product's base wholesale price for this tier."
              }
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Saving…" : edit.tier ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete tier?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{deleteTarget?.name}</strong>? Customers on this tier fall back
            to the default tier, and any per-product price overrides for it are removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WholesaleTiersPage;
